import { useEffect, useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { verifyEmail } from "@/lib/api/auth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@repo/ui";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { motion } from "framer-motion";

const verifyEmailSearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute("/_auth/verify-email")({
  validateSearch: verifyEmailSearchSchema,
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { token } = useSearch({ from: "/_auth/verify-email" });
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing verification token. Please check your email link.");
      return;
    }

    let cancelled = false;

    async function verify() {
      try {
        const data = (await verifyEmail({ token: token! })) as {
          success: boolean;
          isLoggedIn: boolean;
          message: string;
        };
        if (cancelled) return;
        setStatus("success");
        setMessage(data.message);
        setIsLoggedIn(data.isLoggedIn);
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        const errorData = err as { message?: string; error?: string };
        setMessage(
          errorData.message ||
            errorData.error ||
            "Verification failed. The link may be invalid or expired."
        );
      }
    }

    verify();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted/20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <img
              src="/logo-with-text-blue.png"
              alt="C2 Vault"
              className="mb-4 h-6 w-auto mx-auto"
            />
            <CardTitle className="text-lg font-semibold">
              Email Verification
            </CardTitle>
            <CardDescription className="text-sm mt-1">
              {status === "loading" && "Verifying your email address..."}
              {status === "success" && "Your email has been verified"}
              {status === "error" && "Verification failed"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4 pt-4">
            {status === "loading" && (
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
            )}

            {status === "success" && (
              <>
                <CheckCircle2 className="h-10 w-10 text-green-600" />
                <p className="text-sm text-center text-muted-foreground">
                  {message}
                </p>
                <Link
                  to={isLoggedIn ? "/dashboard" : "/login"}
                  className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
                >
                  {isLoggedIn ? "Continue to App" : "Go to Login"}
                </Link>
              </>
            )}

            {status === "error" && (
              <>
                <XCircle className="h-10 w-10 text-destructive" />
                <p className="text-sm text-center text-muted-foreground">
                  {message}
                </p>
                <Link
                  to="/login"
                  className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
                >
                  Go to Login
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
